import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import Loader from '../../../../components/Looder';
import { notify } from '../../../../utils/notification';
import { SOCIETY_CONTROLLER, SOCIETY_MANAGEMENT } from '../../../../Routing/routes';
import {
    getCommunicationSettingsReq,
    updateCommunicationSettingsReq,
    communicationSettingErrorMessage,
} from '../../../../axios/request/SocietyController/CtrlCommunicationSettingReqs/CtrlCommunicationSettingReqs';

const CHANNEL_ICON = { EMAIL: 'mdi:email-outline', SMS: 'mdi:message-text-outline' };

// Only the switches that differ from what was loaded, so PUT carries real changes.
const diffSettings = (original, edited) => (edited?.document_types || []).flatMap((doc) => {
    const before = original?.document_types?.find((d) => d.code === doc.code);
    return Object.entries(doc.channels || {})
        .filter(([channel, enabled]) => before?.channels?.[channel] !== enabled)
        .map(([channel, enabled]) => ({ document_type: doc.code, channel, is_enabled: enabled }));
});

/**
 * Per-community allow/deny switches for sending billing documents by channel —
 * GET/PUT /api/communication-settings. Rows and columns come from the response,
 * so new document types or channels appear without a frontend change.
 */
const CommunicationSettingsLayer = ({ societyId, societyName }) => {
    const navigate = useNavigate();
    const [original, setOriginal] = useState(null);
    const [edited, setEdited] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState('');

    const load = useCallback(() => {
        if (societyId == null) return undefined;
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        getCommunicationSettingsReq(societyId)
            .then((res) => {
                if (cancelled) return;
                if (res?.success === false) throw new Error(res?.message);
                setOriginal(res?.data || null);
                setEdited(res?.data || null);
            })
            .catch((err) => { if (!cancelled) setLoadError(communicationSettingErrorMessage(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [societyId]);

    useEffect(() => load(), [load]);

    const changes = useMemo(() => diffSettings(original, edited), [original, edited]);
    const channels = edited?.channels || [];
    const docs = edited?.document_types || [];
    const busy = loading || saving;

    const toggle = (docCode, channel, value) => {
        setEdited((prev) => ({
            ...prev,
            document_types: prev.document_types.map((d) => (d.code === docCode
                ? { ...d, channels: { ...d.channels, [channel]: value } }
                : d)),
        }));
    };

    // Flip a whole column on/off.
    const setColumn = (channel, value) => {
        setEdited((prev) => ({
            ...prev,
            document_types: prev.document_types.map((d) => ({ ...d, channels: { ...d.channels, [channel]: value } })),
        }));
    };

    const handleSave = async () => {
        if (!changes.length) return;
        setSaving(true);
        try {
            const res = await updateCommunicationSettingsReq(societyId, changes);
            if (res?.success === false) throw new Error(res?.message);
            // The response is the resolved matrix — it becomes the new baseline.
            setOriginal(res?.data || edited);
            setEdited(res?.data || edited);
            notify('success', res?.message || 'Communication settings updated successfully');
        } catch (err) {
            notify('error', communicationSettingErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const enabledCount = (code) => docs.filter((d) => d.channels?.[code]).length;

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
            {/* Header */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                    <span className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-rose-50 tw-text-rose-500 tw-inline-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <Icon icon="solar:chat-round-dots-linear" className="tw-text-xl" />
                    </span>
                    <div className="tw-min-w-0">
                        <div className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate">
                            {societyName || `Community #${societyId}`}
                        </div>
                        <div className="tw-text-xs tw-text-gray-500">
                            Choose which billing documents members can be sent, per channel. This only sets permission — nothing is sent from here.
                        </div>
                    </div>
                </div>
                <button onClick={() => navigate(SOCIETY_CONTROLLER + SOCIETY_MANAGEMENT)}
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                    <Icon icon="mdi:arrow-left" className="tw-text-sm" />
                    Back to Communities
                </button>
            </div>

            {/* Body */}
            <div className="tw-p-5">
                {loading ? (
                    <div className="tw-py-16 tw-flex tw-justify-center"><Loader /></div>
                ) : loadError ? (
                    <div className="tw-py-14 tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <Icon icon="mdi:alert-circle-outline" className="tw-text-4xl tw-text-red-300" />
                        <p className="tw-text-xs tw-text-red-500 tw-text-center tw-mb-1">{loadError}</p>
                        <button onClick={load}
                            className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                            <Icon icon="mdi:refresh" className="tw-text-sm" /> Retry
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Per-channel summary */}
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mb-4">
                            {channels.map((c) => {
                                const on = enabledCount(c.code);
                                const allOn = docs.length > 0 && on === docs.length;
                                return (
                                    <div key={c.code} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-gray-100 tw-bg-slate-50/60">
                                        <div className="tw-flex tw-items-center tw-gap-2.5">
                                            <span className="tw-w-8 tw-h-8 tw-rounded-md tw-bg-white tw-border tw-border-gray-100 tw-text-gray-600 tw-inline-flex tw-items-center tw-justify-center">
                                                <Icon icon={CHANNEL_ICON[c.code] || 'mdi:bell-outline'} className="tw-text-base" />
                                            </span>
                                            <div>
                                                <div className="tw-text-xs tw-font-semibold tw-text-gray-800">{c.label}</div>
                                                <div className="tw-text-[11px] tw-text-gray-500">{on} of {docs.length} documents allowed</div>
                                            </div>
                                        </div>
                                        <button type="button" onClick={() => setColumn(c.code, !allOn)} disabled={busy}
                                            className="tw-text-[11px] tw-font-medium tw-text-rose-500 hover:tw-text-rose-600 disabled:tw-opacity-50">
                                            {allOn ? 'Turn all off' : 'Turn all on'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Matrix */}
                        <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-xs">
                                <thead>
                                    <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                        <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Document</th>
                                        {channels.map((c) => (
                                            <th key={c.code} className="tw-px-4 tw-py-3 tw-text-center tw-w-40 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">
                                                <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                    <Icon icon={CHANNEL_ICON[c.code] || 'mdi:bell-outline'} className="tw-text-xs" />
                                                    {c.label}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="tw-divide-y tw-divide-gray-50">
                                    {docs.map((d) => (
                                        <tr key={d.code} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                            <td className="tw-px-4 tw-py-3 tw-font-medium tw-text-gray-800">{d.label}</td>
                                            {channels.map((c) => {
                                                const on = !!d.channels?.[c.code];
                                                const changed = original?.document_types?.find((o) => o.code === d.code)?.channels?.[c.code] !== d.channels?.[c.code];
                                                return (
                                                    <td key={c.code} className="tw-px-4 tw-py-3">
                                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                                                            <ToggleSwitch size="sm" id={`comm-${d.code}-${c.code}`}
                                                                checked={on} disabled={busy}
                                                                onChange={(v) => toggle(d.code, c.code, v)} />
                                                            <span className={`tw-w-12 tw-text-left tw-text-[11px] ${on ? 'tw-text-emerald-600' : 'tw-text-gray-400'}`}>
                                                                {on ? 'Allowed' : 'Denied'}
                                                            </span>
                                                            <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${changed ? 'tw-bg-amber-400' : 'tw-bg-transparent'}`}
                                                                title={changed ? 'Unsaved change' : undefined} />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {channels.some((c) => c.code === 'SMS') && (
                            <p className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-amber-700 tw-mt-3 tw-mb-0">
                                <Icon icon="mdi:information-outline" className="tw-text-sm" />
                                SMS may incur per-message charges.
                            </p>
                        )}
                    </>
                )}
            </div>

            {/* Footer */}
            {!loading && !loadError && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-slate-50/60">
                    <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] ${changes.length ? 'tw-text-amber-700' : 'tw-text-gray-400'}`}>
                        {changes.length > 0 && <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-amber-400" />}
                        {changes.length
                            ? `${changes.length} unsaved change${changes.length === 1 ? '' : 's'}`
                            : 'All changes saved'}
                    </span>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button onClick={() => setEdited(original)} disabled={busy || !changes.length}
                            className="tw-h-8 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                            Discard
                        </button>
                        <button onClick={handleSave} disabled={busy || !changes.length}
                            className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                            {saving && <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />}
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommunicationSettingsLayer;
